import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as AttachMoneyIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import * as XLSX from 'xlsx';
import { getCurrentMonthYearBrasilia } from '../utils/dateUtils';
import apiService from '../services/api';

interface BillingStats {
  summary: {
    totalSalesCount: number;
    totalMaterialValue: number;
    totalFreightValue: number;
    totalValue: number;
    changes: {
      salesCount: string;
      material: string;
      freight: string;
      total: string;
    };
  };
  salesBySeller: Array<{
    id: string;
    name: string;
    email: string;
    salesCount: number;
    totalMaterial: number;
    totalFreight: number;
    totalValue: number;
  }>;
  monthlyHistory: Array<{
    month: string;
    year: number;
    comercial: number;
    total: number;
  }>;
  dailyHistory?: Array<{
    date: string;
    label: string;
    comercial: number;
    total: number;
  }>;
  month: number;
  year: number;
  startDate?: string;
  endDate?: string;
}

type PeriodType = 'month' | 'day';

const Billing: React.FC = () => {
  const { month: currentMonth, year: currentYear } = getCurrentMonthYearBrasilia();
  const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  const [periodType, setPeriodType] = useState<PeriodType>('month');
  const [month, setMonth] = useState(currentMonth.toString());
  const [year, setYear] = useState(currentYear.toString());
  const [selectedDate, setSelectedDate] = useState(today);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<BillingStats | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const formatCurrency = (value: number) => {
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      const params: { month?: number; year?: number; startDate?: string; endDate?: string } = {
        month: parseInt(month),
        year: parseInt(year),
      };
      if (periodType === 'day') {
        params.startDate = selectedDate;
        params.endDate = selectedDate;
      }
      const data = await apiService.getBillingStats(params);
      setStats(data);
    } catch (error: any) {
      console.error('Erro ao carregar estatísticas:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar estatísticas de faturamento',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [month, year, periodType, selectedDate]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleRefresh = () => {
    loadStats();
  };

  const handleExport = async () => {
    try {
      setSnackbar({
        open: true,
        message: 'Gerando relatório completo...',
        severity: 'success',
      });

      const exportMonth = periodType === 'day'
        ? parseInt(selectedDate.slice(5, 7))
        : parseInt(month);
      const exportYear = periodType === 'day'
        ? parseInt(selectedDate.slice(0, 4))
        : parseInt(year);

      const report = await apiService.getFullReport(exportMonth, exportYear);
      const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
      const periodoLabel = periodType === 'day'
        ? selectedDate.split('-').reverse().join('/')
        : `${monthNames[exportMonth - 1]} de ${exportYear}`;
      
      // Criar workbook
      const wb = XLSX.utils.book_new();

      // ========== ABA 1: RESUMO GERAL ==========
      const resumoData = [
        ['RELATÓRIO MENSAL - VETTORPRINT'],
        [`Período: ${periodoLabel}`],
        [''],
        ['RESUMO GERAL'],
        [''],
        ['VENDAS'],
        ['Quantidade de Vendas', report.totais.vendas.quantidade],
        ['Valor Material', report.totais.vendas.valorMaterial],
        ['Valor Frete', report.totais.vendas.valorFrete],
        ['Valor Total', report.totais.vendas.valorTotal],
        [''],
        ['ARTES/DESIGN'],
        ['Total de Pedidos', report.totais.artes.totalPedidos],
        ['Artes Concluídas', report.totais.artes.concluidas],
        ['Artes em Andamento', report.totais.artes.emAndamento],
        ['Artes Pendentes', report.totais.artes.pendentes],
        [''],
        ['OPERAÇÕES'],
        ['Erros Internos', report.totais.errosInternos.quantidade, 'Valor Total:', report.totais.errosInternos.valorTotal],
        ['Pedidos Estornados', report.totais.estornos.quantidade, 'Valor Total:', report.totais.estornos.valorTotal],
        ['Pedidos Extraviados', report.totais.extraviados.quantidade, 'Valor Total:', report.totais.extraviados.valorTotal],
      ];
      const wsResumo = XLSX.utils.aoa_to_sheet(resumoData);
      wsResumo['!cols'] = [{ wch: 25 }, { wch: 20 }, { wch: 15 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, wsResumo, 'Resumo Geral');

      // ========== ABA 2: VENDAS POR VENDEDOR ==========
      const vendedoresHeader = [['VENDAS POR VENDEDOR'], ['']];
      const vendedoresColumns = [['Posição', 'Nome', 'Email', 'Qtd Vendas', 'Valor Material', 'Valor Frete', 'Valor Total']];
      const vendedoresRows = report.salesBySeller.map((v: any, i: number) => [
        `${i + 1}º`,
        v.nome,
        v.email,
        v.quantidadeVendas,
        v.valorMaterial,
        v.valorFrete,
        v.valorTotal,
      ]);
      const wsVendedores = XLSX.utils.aoa_to_sheet([...vendedoresHeader, ...vendedoresColumns, ...vendedoresRows]);
      wsVendedores['!cols'] = [{ wch: 10 }, { wch: 25 }, { wch: 30 }, { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, wsVendedores, 'Vendas por Vendedor');

      // ========== ABA 3: VENDAS DETALHADAS ==========
      if (report.vendasDetalhadas.length > 0) {
        const vendasHeader = [['VENDAS DETALHADAS'], ['']];
        const vendasColumns = [['Data', 'Vendedor', 'Tel. Cliente', 'Valor Material', 'Valor Frete', 'Valor Total', 'Observações']];
        const vendasRows = report.vendasDetalhadas.map((v: any) => [
          v.data,
          v.vendedor,
          v.telefoneCliente,
          v.valorMaterial,
          v.valorFrete,
          v.valorTotal,
          v.observacoes,
        ]);
        const wsVendas = XLSX.utils.aoa_to_sheet([...vendasHeader, ...vendasColumns, ...vendasRows]);
        wsVendas['!cols'] = [{ wch: 12 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 40 }];
        XLSX.utils.book_append_sheet(wb, wsVendas, 'Vendas Detalhadas');
      }

      // ========== ABA 4: ARTES POR DESIGNER ==========
      const designersHeader = [['ARTES POR DESIGNER'], ['']];
      const designersColumns = [['Posição', 'Nome', 'Email', 'Total Pedidos', 'Concluídas', 'Em Andamento', 'Pendentes']];
      const designersRows = report.artesPorDesigner.map((d: any, i: number) => [
        `${i + 1}º`,
        d.nome,
        d.email,
        d.totalPedidos,
        d.artesConcluidas,
        d.artesEmAndamento,
        d.artesPendentes,
      ]);
      const wsDesigners = XLSX.utils.aoa_to_sheet([...designersHeader, ...designersColumns, ...designersRows]);
      wsDesigners['!cols'] = [{ wch: 10 }, { wch: 25 }, { wch: 30 }, { wch: 15 }, { wch: 12 }, { wch: 15 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(wb, wsDesigners, 'Artes por Designer');

      // ========== ABA 5: ERROS INTERNOS ==========
      if (report.errosInternos.length > 0) {
        const errosHeader = [['ERROS INTERNOS'], ['']];
        const errosColumns = [['Data', 'Nº Pedido', 'Departamento', 'Responsável', 'Descrição', 'Valor']];
        const errosRows = report.errosInternos.map((e: any) => [
          e.data,
          e.numeroPedido,
          e.departamento,
          e.responsavel,
          e.descricao,
          e.valor,
        ]);
        const wsErros = XLSX.utils.aoa_to_sheet([...errosHeader, ...errosColumns, ...errosRows]);
        wsErros['!cols'] = [{ wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 20 }, { wch: 40 }, { wch: 12 }];
        XLSX.utils.book_append_sheet(wb, wsErros, 'Erros Internos');
      }

      // ========== ABA 6: PEDIDOS ESTORNADOS ==========
      if (report.pedidosEstornados.length > 0) {
        const estornosHeader = [['PEDIDOS ESTORNADOS'], ['']];
        const estornosColumns = [['Data', 'Nº Pedido', 'Tipo', 'Cliente', 'Motivo', 'Valor', 'Status']];
        const estornosRows = report.pedidosEstornados.map((e: any) => [
          e.data,
          e.numeroPedido,
          e.tipo,
          e.cliente,
          e.motivo,
          e.valor,
          e.status,
        ]);
        const wsEstornos = XLSX.utils.aoa_to_sheet([...estornosHeader, ...estornosColumns, ...estornosRows]);
        wsEstornos['!cols'] = [{ wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 20 }, { wch: 40 }, { wch: 12 }, { wch: 12 }];
        XLSX.utils.book_append_sheet(wb, wsEstornos, 'Pedidos Estornados');
      }

      // ========== ABA 7: PEDIDOS EXTRAVIADOS ==========
      if (report.pedidosExtraviados.length > 0) {
        const extraviadosHeader = [['PEDIDOS EXTRAVIADOS'], ['']];
        const extraviadosColumns = [['Data', 'Nº Pedido', 'Transportadora', 'Valor Frete', 'Valor Pedido']];
        const extraviadosRows = report.pedidosExtraviados.map((e: any) => [
          e.data,
          e.numeroPedido,
          e.transportadora,
          e.valorFrete,
          e.valorPedido,
        ]);
        const wsExtraviados = XLSX.utils.aoa_to_sheet([...extraviadosHeader, ...extraviadosColumns, ...extraviadosRows]);
        wsExtraviados['!cols'] = [{ wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];
        XLSX.utils.book_append_sheet(wb, wsExtraviados, 'Pedidos Extraviados');
      }

      // Gerar e baixar arquivo
      const fileName = periodType === 'day'
        ? `Relatorio_VettorPrint_${selectedDate.replace(/-/g, '')}.xlsx`
        : `Relatorio_VettorPrint_${monthNames[exportMonth - 1]}_${exportYear}.xlsx`;
      XLSX.writeFile(wb, fileName);

      setSnackbar({
        open: true,
        message: `Relatório "${fileName}" exportado com sucesso!`,
        severity: 'success',
      });
    } catch (error: any) {
      console.error('Erro ao exportar:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao exportar relatório. Tente novamente.',
        severity: 'error',
      });
    }
  };

  const getChangeColor = (change: string) => {
    const value = parseFloat(change);
    if (value > 0) return { bg: '#d1fae5', color: '#10b981', prefix: '+' };
    if (value < 0) return { bg: '#fee2e2', color: '#ef4444', prefix: '' };
    return { bg: '#f3f4f6', color: '#6b7280', prefix: '' };
  };

  // Preparar dados para o gráfico de barras
  const chartData = stats?.salesBySeller.map(seller => ({
    name: seller.name.split(' ')[0], // Apenas primeiro nome
    value: seller.totalMaterial,
  })) || [];

  // Preparar dados para o gráfico: diário (7 dias) ou mensal (6 meses)
  const chartDataHistory = stats?.dailyHistory || stats?.monthlyHistory || [];
  const isDailyView = !!stats?.dailyHistory?.length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress sx={{ color: '#f59e0b' }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
            Faturamento Comercial
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Análise de vendas por vendedor e distribuição mensal
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Período</InputLabel>
            <Select
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value as PeriodType)}
              label="Período"
            >
              <MenuItem value="month">Mês inteiro</MenuItem>
              <MenuItem value="day">Dia específico</MenuItem>
            </Select>
          </FormControl>

          {periodType === 'month' && (
            <>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Mês</InputLabel>
                <Select value={month} onChange={(e) => setMonth(e.target.value)} label="Mês">
                  <MenuItem value="1">Janeiro</MenuItem>
                  <MenuItem value="2">Fevereiro</MenuItem>
                  <MenuItem value="3">Março</MenuItem>
                  <MenuItem value="4">Abril</MenuItem>
                  <MenuItem value="5">Maio</MenuItem>
                  <MenuItem value="6">Junho</MenuItem>
                  <MenuItem value="7">Julho</MenuItem>
                  <MenuItem value="8">Agosto</MenuItem>
                  <MenuItem value="9">Setembro</MenuItem>
                  <MenuItem value="10">Outubro</MenuItem>
                  <MenuItem value="11">Novembro</MenuItem>
                  <MenuItem value="12">Dezembro</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Ano</InputLabel>
                <Select value={year} onChange={(e) => setYear(e.target.value)} label="Ano">
                  <MenuItem value="2023">2023</MenuItem>
                  <MenuItem value="2024">2024</MenuItem>
                  <MenuItem value="2025">2025</MenuItem>
                  <MenuItem value="2026">2026</MenuItem>
                </Select>
              </FormControl>
            </>
          )}

          {periodType === 'day' && (
            <TextField
              size="small"
              label="Data"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
          )}

          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{ textTransform: 'none' }}
          >
            Atualizar
          </Button>

          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{ textTransform: 'none' }}
          >
            Exportar
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    backgroundColor: '#dbeafe',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#3b82f6',
                  }}
                >
                  <BarChartIcon />
                </Box>
                {stats && (
                  <Chip
                    label={`${getChangeColor(stats.summary.changes.salesCount).prefix}${stats.summary.changes.salesCount}%`}
                    size="small"
                    sx={{
                      backgroundColor: getChangeColor(stats.summary.changes.salesCount).bg,
                      color: getChangeColor(stats.summary.changes.salesCount).color,
                      fontWeight: 600,
                      fontSize: '11px',
                    }}
                  />
                )}
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                {stats?.summary.totalSalesCount || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total de Vendas
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    backgroundColor: '#e0e7ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6366f1',
                  }}
                >
                  <TrendingUpIcon />
                </Box>
                {stats && (
                  <Chip
                    label={`${getChangeColor(stats.summary.changes.material).prefix}${stats.summary.changes.material}%`}
                    size="small"
                    sx={{
                      backgroundColor: getChangeColor(stats.summary.changes.material).bg,
                      color: getChangeColor(stats.summary.changes.material).color,
                      fontWeight: 600,
                      fontSize: '11px',
                    }}
                  />
                )}
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                {formatCurrency(stats?.summary.totalMaterialValue || 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Valor sem Frete
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    backgroundColor: '#fce7f3',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ec4899',
                  }}
                >
                  <AttachMoneyIcon />
                </Box>
                {stats && (
                  <Chip
                    label={`${getChangeColor(stats.summary.changes.freight).prefix}${stats.summary.changes.freight}%`}
                    size="small"
                    sx={{
                      backgroundColor: getChangeColor(stats.summary.changes.freight).bg,
                      color: getChangeColor(stats.summary.changes.freight).color,
                      fontWeight: 600,
                      fontSize: '11px',
                    }}
                  />
                )}
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                {formatCurrency(stats?.summary.totalFreightValue || 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Valor Frete
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '12px',
                    backgroundColor: '#fef3c7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#f59e0b',
                  }}
                >
                  <PeopleIcon />
                </Box>
                {stats && (
                  <Chip
                    label={`${getChangeColor(stats.summary.changes.total).prefix}${stats.summary.changes.total}%`}
                    size="small"
                    sx={{
                      backgroundColor: getChangeColor(stats.summary.changes.total).bg,
                      color: getChangeColor(stats.summary.changes.total).color,
                      fontWeight: 600,
                      fontSize: '11px',
                    }}
                  />
                )}
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                {formatCurrency(stats?.summary.totalValue || 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Valor Total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Vendas por Vendedor */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <PeopleIcon sx={{ color: '#3b82f6' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Vendas por Vendedor
                </Typography>
              </Box>
              
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                    <Bar dataKey="value" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <Typography color="text.secondary">
                    Nenhuma venda registrada no período
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Histórico Mensal ou Diário */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <TrendingUpIcon sx={{ color: '#10b981' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {isDailyView ? 'Últimos 7 dias' : 'Histórico Mensal (Últimos 6 meses)'}
                </Typography>
              </Box>
              
              {chartDataHistory.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartDataHistory}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey={isDailyView ? 'label' : 'month'} />
                    <YAxis tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                    <Bar dataKey="comercial" name="Faturamento" fill="#10b981" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                  <Typography color="text.secondary">
                    Nenhum dado histórico disponível
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Tabela de Vendedores */}
        <Grid item xs={12}>
          <Card sx={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Ranking de Vendedores
              </Typography>
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Posição</strong></TableCell>
                      <TableCell><strong>Vendedor</strong></TableCell>
                      <TableCell align="center"><strong>Qtd Vendas</strong></TableCell>
                      <TableCell align="right"><strong>Valor Material</strong></TableCell>
                      <TableCell align="right"><strong>Valor Frete</strong></TableCell>
                      <TableCell align="right"><strong>Valor Total</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats?.salesBySeller.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="text.secondary">
                            Nenhum vendedor com vendas no período
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      stats?.salesBySeller.map((seller, index) => (
                        <TableRow key={seller.id} hover>
                          <TableCell>
                            <Chip
                              label={`${index + 1}º`}
                              size="small"
                              sx={{
                                backgroundColor: index === 0 ? '#fef3c7' : index === 1 ? '#f3f4f6' : index === 2 ? '#fed7aa' : '#f3f4f6',
                                color: index === 0 ? '#f59e0b' : index === 1 ? '#6b7280' : index === 2 ? '#ea580c' : '#6b7280',
                                fontWeight: 700,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {seller.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {seller.email}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={seller.salesCount}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(seller.totalMaterial)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(seller.totalFreight)}
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontWeight: 600, color: '#10b981' }}>
                              {formatCurrency(seller.totalValue)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Billing;
